// src/components/ui/MapContainer.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FaMapMarkerAlt, FaRoute } from "react-icons/fa";
import LocationMap from "../GoogleMap/LocationMap";

interface MapContainerProps {
  restaurantLocation?: {
    coordinates: string;
    google_maps_link: string;
  } | null;
  userLocation?: string | null;
  restaurantName?: string;
  restaurantLogo?: string | null; // Add logo prop for restaurant marker
  placeId?: string | null; // Google Places ID for fetching reviews
  theme?: {
    primaryColor: string;
    textColor: string;
    backgroundColor: string;
  };
  title?: string;
  subtitle?: string;
  showForPickup?: boolean;
  className?: string;
  onGetUserLocation?: () => void; // New prop for getting user location
  showReviews?: boolean; // Whether to show reviews section
}

const MapContainer: React.FC<MapContainerProps> = ({
  restaurantLocation,
  userLocation,
  restaurantName,
  restaurantLogo,
  placeId,
  theme,
  title,
  subtitle,
  showForPickup = false,
  className = "",
  onGetUserLocation,
  showReviews = false,
}) => {
  const { t } = useTranslation();

  // Set default title based on pickup mode if not provided
  const defaultTitle = showForPickup
    ? t("menu.cart.location.pickupLocation")
    : t("menu.cart.location.locationMap");

  const finalTitle = title || defaultTitle;

  // For pickup, we only show restaurant location
  const displayUserLocation = showForPickup ? null : userLocation;

  if (!restaurantLocation && !displayUserLocation) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={`bg-white rounded-xl overflow-hidden border shadow-lg ${className}`}
      style={{
        borderColor: `${theme?.primaryColor || "#ea580c"}20`,
        boxShadow: `0 4px 20px ${theme?.primaryColor || "#ea580c"}10`,
      }}
    >
      {/* Header */}
      <div
        className="p-4 text-white relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${
            theme?.primaryColor || "#ea580c"
          }, ${theme?.primaryColor || "#ea580c"}dd)`,
        }}
      >
        {/* Background pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 80%, ${
              theme?.backgroundColor || "#ffffff"
            } 2px, transparent 2px), radial-gradient(circle at 80% 20%, ${
              theme?.backgroundColor || "#ffffff"
            } 2px, transparent 2px)`,
            backgroundSize: "20px 20px",
          }}
        />

        <div className="relative flex items-center gap-3">
          <motion.div
            whileHover={{ rotate: [0, -10, 10, 0] }}
            transition={{ duration: 0.5 }}
          >
            {showForPickup ? (
              <FaMapMarkerAlt size={20} />
            ) : (
              <FaRoute size={20} />
            )}
          </motion.div>
          <div>
            <h3 className="font-semibold text-lg">{finalTitle}</h3>
            {subtitle && <p className="text-sm opacity-90">{subtitle}</p>}
          </div>
        </div>
      </div>

      {/* Map Content */}
      <div className="p-4">
        <LocationMap
          restaurantLocation={restaurantLocation}
          userLocation={displayUserLocation}
          restaurantName={restaurantName}
          restaurantLogo={restaurantLogo}
          placeId={placeId}
          theme={theme}
          className="rounded-lg overflow-hidden"
          isPickupMode={showForPickup}
          onGetUserLocation={onGetUserLocation}
          showReviews={showReviews}
        />
      </div>

      {/* Info Footer */}
      <div className="px-4 pb-4">
        <div className="flex items-center justify-center gap-4 text-xs">
          {restaurantLocation && (
            <motion.div
              className="flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
            >
              <div
                className="w-3 h-3 rounded-full border border-white"
                style={{ backgroundColor: theme?.primaryColor || "#ea580c" }}
              />
              <span style={{ color: theme?.textColor || "#374151" }}>
                {showForPickup
                  ? t("menu.cart.location.pickupLocation")
                  : restaurantName || t("menu.cart.location.restaurant")}
              </span>
            </motion.div>
          )}
          {displayUserLocation && (
            <motion.div
              className="flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
            >
              <div className="w-3 h-3 rounded-full bg-green-500 border border-white" />
              <span style={{ color: theme?.textColor || "#374151" }}>
                {t("menu.cart.location.yourLocation")}
              </span>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MapContainer;
